import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, Button, Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import { Logo } from '@/src/components/Logo';
import { isReferralCodeShape, normalizeReferralCode } from '@/src/components/referrals/offerCatalogue';

interface Preview { name: string; presentation: string; attribution_ready: boolean }

/**
 * Public referral landing page. Shows the partner's campaign presentation and
 * the standardized D-057 customer benefit, then carries the referral code into
 * registration (attribution is only possible for a NEW account at signup).
 */
export default function CampaignPreview() {
  const params = useLocalSearchParams<{ code?: string; ref?: string }>();
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const router = useRouter();
  const code = typeof params.code === 'string' ? params.code : '';
  // The campaign code is canonical and claimable (the backend resolves it to the
  // active partner); it is what the registration carries forward.
  const referral = normalizeReferralCode(code);
  const [data, setData] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true); setData(null); setError(false);
    void apiFetch<Preview>(`${API_BASE}/api/referrals/campaign-preview/${encodeURIComponent(code)}/`,
      { includeCompanyContext: false }, false, false,
    ).then(response => {
      if (!current) return;
      setLoading(false);
      if (response.ok) setData(response.data); else setError(true);
    });
    return () => { current = false; };
  }, [code, attempt]);

  const start = () => router.push(`/register/choice?ref=${encodeURIComponent(referral)}` as never);

  return <ScrollView style={{ backgroundColor: theme.colors.background }} contentContainerStyle={{ padding: 16, gap: 16, maxWidth: 720, width: '100%', alignSelf: 'center' }}>
    <View testID="partner-brand" accessible accessibilityLabel="AllDrivers"><Logo size="medium" /></View>
    {loading && <ActivityIndicator />}
    {error && <Card><Card.Content style={{ gap: 12 }}>
      <Text>{t('previewUnavailable')}</Text>
      <Button contentStyle={{ minHeight: 44 }} onPress={() => setAttempt(value => value + 1)}>{t('retry')}</Button>
    </Card.Content></Card>}
    {data && <Card testID="campaign-preview"><Card.Content style={{ gap: 16 }}>
      <Text variant="headlineMedium">{data.name}</Text>
      {!!data.presentation && <Text variant="bodyLarge">{data.presentation}</Text>}
      <View testID="customer-benefit" style={{ gap: 6, padding: 16, borderRadius: 16, backgroundColor: theme.colors.secondaryContainer }}>
        <Text variant="titleMedium" style={{ color: theme.colors.onSecondaryContainer }}>{t('buyerBenefitTitle')}</Text>
        <Text variant="bodyMedium" style={{ color: theme.colors.onSecondaryContainer }}>{t('buyerMonthly')}</Text>
        <Text variant="bodyMedium" style={{ color: theme.colors.onSecondaryContainer }}>{t('buyerAnnual')}</Text>
        <Text variant="bodySmall" style={{ color: theme.colors.onSecondaryContainer }}>{t('buyerExcluded')}</Text>
      </View>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('buyerTermsNote')}</Text>
      {isReferralCodeShape(referral)
        ? <Button mode="contained" contentStyle={{ minHeight: 44 }} testID="campaign-register" onPress={start}>{t('buyerCta')}</Button>
        : <Text accessibilityRole="alert">{t('buyerNoCode')}</Text>}
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('buyerExistingAccount')}</Text>
      {data.attribution_ready === false && <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('previewNotice')}</Text>}
    </Card.Content></Card>}
  </ScrollView>;
}
